import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Plus } from "lucide-react";
import { ASSETS, BLOG_POSTS, LINKS, CATEGORIES } from "../lib/constants";
import { BLOG_CONTENT } from "../lib/blogContent";
import { Reveal } from "../components/Reveal";
import { Label } from "../components/bits";

const cat = (name) => (CATEGORIES.find((c) => c.name === name) || {}).url || LINKS.menu;

const LINK_MAP = {
  about: "/about",
  shop: "/shop",
  contact: "/contact",
  events: "/events",
  signup: "/signup",
  waiver: "/waiver",
  faq: "/",
  menu: LINKS.menu,
  newarrivals: LINKS.menu,
  register: LINKS.registerDC,
  registernon: LINKS.registerNonDC,
  cardguide: "/blog/how-to-get-medical-cannabis-card-dc",
  lounge: "/blog/inside-higher-ground-dc-cannabis-lounge",
  flower: cat("Flower"),
  prerolls: cat("Prerolls"),
  edibles: cat("Edibles"),
  vapes: cat("Cartridges & Vapes"),
  concentrates: cat("Concentrates"),
  tinctures: cat("Tinctures"),
  topicals: LINKS.menu,
  cbd: LINKS.menu,
  lowthc: LINKS.menu,
};

const resolve = (key) => LINK_MAP[key] || key;

// Parse [text](@key) or [text](/path) into React nodes with proper internal/external links.
const renderInline = (text) => {
  const parts = [];
  const regex = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let i = 0;
  while ((m = regex.exec(text)) !== null) {
    if (m.index > last) parts.push(text.slice(last, m.index));
    const label = m[1];
    const raw = m[2];
    const href = raw.startsWith("@") ? resolve(raw.slice(1)) : raw;
    if (href.startsWith("/")) {
      parts.push(
        <Link key={i++} to={href} className="text-gold underline decoration-gold/40 underline-offset-4 transition-colors hover:decoration-gold">
          {label}
        </Link>
      );
    } else {
      parts.push(
        <a key={i++} href={href} target="_blank" rel="noopener noreferrer" className="text-gold underline decoration-gold/40 underline-offset-4 transition-colors hover:decoration-gold">
          {label}
        </a>
      );
    }
    last = regex.lastIndex;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
};

export default function BlogPost() {
  const { slug } = useParams();
  const post = BLOG_POSTS.find((p) => p.slug === slug) || BLOG_POSTS[0];
  const content = BLOG_CONTENT[post.slug];

  useEffect(() => {
    if (!content) return;
    const prevTitle = document.title;
    document.title = content.metaTitle;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc ? metaDesc.getAttribute("content") : null;
    if (metaDesc) metaDesc.setAttribute("content", content.metaDescription);

    const canonical = `https://highergrounddc.com/blog/${post.slug}`;
    const set = (sel, attr, val) => {
      const el = document.querySelector(sel);
      const prev = el ? el.getAttribute(attr) : null;
      if (el) el.setAttribute(attr, val);
      return { el, prev, attr };
    };
    const metas = [
      set('link[rel="canonical"]', "href", canonical),
      set('meta[property="og:title"]', "content", content.metaTitle),
      set('meta[property="og:description"]', "content", content.metaDescription),
      set('meta[property="og:url"]', "content", canonical),
      set('meta[name="twitter:title"]', "content", content.metaTitle),
      set('meta[name="twitter:description"]', "content", content.metaDescription),
    ];

    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.setAttribute("data-blog-ld", "1");
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "BlogPosting",
          headline: content.metaTitle,
          description: content.metaDescription,
          articleSection: post.category,
          author: { "@type": "Organization", name: "Higher Ground DC" },
          publisher: { "@type": "Organization", name: "Higher Ground DC" },
          mainEntityOfPage: canonical,
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://highergrounddc.com/" },
            { "@type": "ListItem", position: 2, name: "Blog", item: "https://highergrounddc.com/blog" },
            { "@type": "ListItem", position: 3, name: post.title, item: canonical },
          ],
        },
        {
          "@type": "FAQPage",
          mainEntity: (content.faqs || []).map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        },
      ],
    });
    document.head.appendChild(ld);

    return () => {
      document.title = prevTitle;
      if (metaDesc && prevDesc !== null) metaDesc.setAttribute("content", prevDesc);
      metas.forEach(({ el, prev, attr }) => {
        if (el && prev !== null) el.setAttribute(attr, prev);
      });
      ld.remove();
    };
  }, [content, post]);

  return (
    <div data-testid="blog-post-page">
      <section className="relative flex min-h-[52vh] items-end overflow-hidden pb-16 pt-40">
        <div className="absolute inset-0">
          <img src={ASSETS.chocPour} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/75 to-ink/40" />
        </div>
        <div className="relative z-10 mx-auto w-full max-w-[820px] px-6 lg:px-12">
          <Reveal>
            <Link to="/blog" data-testid="blog-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white/60 transition-colors hover:text-gold">
              <ArrowLeft className="h-4 w-4" /> All Articles
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <Label className="mt-6">{post.category}{content ? ` · ${content.readTime}` : ""}</Label>
          </Reveal>
          <Reveal delay={0.15}>
            <h1 className="mt-4 font-display text-3xl font-light uppercase leading-tight tracking-[0.05em] text-white md:text-5xl">{post.title}</h1>
          </Reveal>
        </div>
      </section>

      <article className="bg-ink py-16 md:py-24" data-testid="blog-article">
        <div className="mx-auto max-w-[760px] px-6 lg:px-12">
          {content ? (
            <>
              {content.blocks.map((block, i) => {
                const [type, value] = block;
                if (type === "h2") return <Reveal key={i}><h2 className="mt-12 font-display text-2xl font-light uppercase tracking-[0.06em] text-white md:text-3xl">{value}</h2></Reveal>;
                if (type === "h3") return <Reveal key={i}><h3 className="mt-8 text-lg font-medium tracking-wide text-gold">{value}</h3></Reveal>;
                if (type === "ul") return (
                  <Reveal key={i}>
                    <ul className="mt-5 space-y-3">
                      {value.map((item, j) => (
                        <li key={j} className="flex gap-3 leading-relaxed text-white/70">
                          <span className="mt-2.5 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-gold" />
                          <span>{renderInline(item)}</span>
                        </li>
                      ))}
                    </ul>
                  </Reveal>
                );
                return <Reveal key={i}><p className="mt-5 text-base leading-relaxed text-white/75 md:text-lg">{renderInline(value)}</p></Reveal>;
              })}

              {content.faqs && content.faqs.length > 0 && (
                <Reveal>
                  <div className="mt-16 border-t border-white/10 pt-12" data-testid="blog-faqs">
                    <Label>Frequently Asked</Label>
                    <div className="mt-6">
                      {content.faqs.map((f, i) => (
                        <details key={i} className="group border-b border-white/10 py-5" data-testid={`blog-faq-${i}`}>
                          <summary className="flex cursor-pointer list-none items-center justify-between gap-6 text-base text-white md:text-lg">
                            {f.q}
                            <Plus className="h-4 w-4 flex-shrink-0 text-gold transition-transform group-open:rotate-45" />
                          </summary>
                          <p className="mt-3 leading-relaxed text-white/60">{f.a}</p>
                        </details>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}

              {content.cta && (
                <Reveal>
                  <div className="mt-14 border border-white/10 bg-ink-2 p-8 text-center md:p-10">
                    <p className="text-base leading-relaxed text-white/80 md:text-lg">{renderInline(content.cta)}</p>
                    <div className="mt-8 flex flex-wrap justify-center gap-4">
                      <a href={LINKS.menu} target="_blank" rel="noopener noreferrer" data-testid="blogpost-menu-btn" className="inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne">
                        Shop the Menu <ArrowUpRight className="h-4 w-4" />
                      </a>
                      <Link to="/blog" data-testid="blogpost-more-btn" className="inline-flex items-center gap-2 border border-white/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:border-gold hover:text-gold">
                        More Articles
                      </Link>
                    </div>
                  </div>
                </Reveal>
              )}
            </>
          ) : (
            <>
              <Reveal><p className="text-center text-lg leading-relaxed text-white/70">{post.excerpt}</p></Reveal>
              <Reveal delay={0.1}>
                <div className="my-10 border-y border-white/10 py-10 text-center">
                  <p className="font-display text-xl font-light uppercase tracking-[0.12em] text-gold">Full Article Coming Soon</p>
                  <p className="mx-auto mt-4 max-w-lg leading-relaxed text-white/55">We're crafting this piece with the same care we bring to our shelves.</p>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="flex flex-wrap justify-center gap-4">
                  <a href={LINKS.menu} target="_blank" rel="noopener noreferrer" data-testid="blogpost-menu-btn" className="inline-flex items-center gap-2 bg-gold px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-ink transition-colors hover:bg-champagne">
                    Shop the Menu <ArrowUpRight className="h-4 w-4" />
                  </a>
                  <Link to="/signup" data-testid="blogpost-signup-btn" className="inline-flex items-center gap-2 border border-white/25 px-8 py-4 text-xs font-semibold uppercase tracking-[0.22em] text-white transition-colors hover:border-gold hover:text-gold">
                    Get Updates
                  </Link>
                </div>
              </Reveal>
            </>
          )}
        </div>
      </article>
    </div>
  );
}
